import { Link } from "react-router-dom";
import { ArrowLeft, Zap } from "lucide-react";
import { BRAND } from "@/lib/constants";
import { useSeo } from "@/lib/seo";

const DISCORD_URL = "https://discord.gg/R5hEsssQgd";

const Section = ({ title, children }) => (
  <section className="mb-12">
    <h2
      className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-5"
      style={{ fontFamily: "'Orbitron', sans-serif" }}
    >
      <span className="text-orange-500 mr-3">/</span>
      {title}
    </h2>
    <div className="text-zinc-300 leading-relaxed text-base sm:text-[17px] space-y-4">
      {children}
    </div>
  </section>
);

export default function Terms() {
  useSeo({
    title: "Terms & Policy — Flow Tweaks",
    description:
      "The terms covering Flow Tweaks plans, the desktop app, refunds, the referral program and support.",
    path: "/terms",
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="terms-page">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="terms-home-link">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span
              className="font-black tracking-[0.18em] text-sm uppercase"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              {BRAND.name}
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4">
          Legal
        </p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-4 leading-[0.95]"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Terms & Policy
        </h1>
        <p className="text-zinc-500 text-sm uppercase tracking-[0.2em] mb-16">
          Last updated · {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </p>

        <p className="text-zinc-400 text-sm leading-relaxed mb-12 border border-orange-500/20 bg-orange-500/[0.03] p-5">
          PLEASE READ THIS AGREEMENT CAREFULLY. BY DOWNLOADING, INSTALLING, OR USING THE FLOW
          TWEAKS SOFTWARE, OR BY PURCHASING ANY FLOW TWEAKS SERVICE, YOU AGREE TO BE BOUND BY
          THESE TERMS. IF YOU DO NOT AGREE, DO NOT INSTALL OR USE THE SOFTWARE OR SERVICES.
        </p>

        <Section title="1. Parties &amp; Acceptance">
          <p>
            This Agreement is between you ("Customer," "you") and Flow Tweaks, a sole
            proprietorship operating under the laws of the State of Missouri, United States
            ("Flow Tweaks," "we," "us," "our"). You must be at least 18, or the age of majority
            in your jurisdiction, and have legal authority to agree to this Agreement. If you
            are under that age, you may only use the Software or Services with the involvement
            and consent of a parent or legal guardian who agrees to be bound on your behalf.
          </p>
        </Section>

        <Section title="2. What We Provide">
          <p>
            Flow Tweaks provides a Windows desktop application (the "Software") that inspects
            your PC and applies optional, reversible performance-related changes to Windows
            settings, the registry, background services, startup behavior, and related system
            configuration, chosen and initiated by you. The Software runs locally on your
            device and does not grant Flow Tweaks remote access to or control of your device.
          </p>
          <p>
            Console optimization plans are handled separately from the Software, through a
            supervised session as agreed with you at the time of purchase.
          </p>
        </Section>

        <Section title="3. License Grant">
          <p>
            We grant you a limited, non-exclusive, non-transferable, revocable license to
            download and use the Software on personal computers you own or control, for your
            own personal, non-commercial use. You may not copy, modify, reverse engineer,
            redistribute, sell, or sublicense the Software, or use it to provide optimization
            services to third parties without our prior written consent.
          </p>
        </Section>

        <Section title="4. No Warranty">
          <p>
            THE SOFTWARE AND SERVICES ARE PROVIDED "AS IS," WITHOUT WARRANTY OF ANY KIND,
            EXPRESS OR IMPLIED. WE DO NOT GUARANTEE ANY SPECIFIC INCREASE IN FRAME RATE,
            PERFORMANCE, OR ANY OTHER MEASURABLE OUTCOME — ACTUAL RESULTS DEPEND ENTIRELY ON
            YOUR HARDWARE, SOFTWARE, AND EXISTING CONFIGURATION.
          </p>
        </Section>

        <Section title="5. Limitation of Liability &amp; Assumption of Risk">
          <p>
            Modifying operating system settings, the registry, drivers, power management, and
            background services carries an inherent risk of system instability, boot failure,
            data loss, a reduced security posture for any security-related tweak you choose to
            disable, incompatibility with games or anti-cheat systems, or voided hardware or
            software warranties. You voluntarily assume these risks. To the maximum extent
            permitted by law, Flow Tweaks and its owner are not liable for any indirect,
            incidental, special, or consequential damages — including damage to or failure of
            your hardware, operating system, or peripherals; data loss or corruption; voided
            warranties; or incompatibility with third-party software — arising from your use of
            the Software. Our total liability for any claim will not exceed the amount you
            actually paid us in the 12 months preceding the claim.
          </p>
        </Section>

        <Section title="6. Restore Points &amp; Reversibility">
          <p>
            The Software attempts to create a Windows System Restore point and record the prior
            value of each setting before applying a change, and provides tools to revert
            changes. This is a best-effort mechanism, not a guarantee — System Restore may be
            disabled, limited, or fail on your PC for reasons outside our control.
          </p>
        </Section>

        <Section title="7. Anti-Cheat &amp; Third-Party Software">
          <p>
            We do not modify game files, inject code into games, or provide any tool designed
            to circumvent anti-cheat systems. Every tweak operates at the OS or driver level.
            Some tweaks may still interact with third-party anti-cheat or DRM software in ways
            we can't fully predict — you're responsible for understanding the policies of any
            game or platform you play.
          </p>
        </Section>

        <Section title="8. Payments &amp; Refunds">
          <p>
            Plans are one-time purchases processed securely through Stripe. Because access
            unlocks instantly upon payment, all sales are final except where required by
            applicable law or at our sole discretion. Access codes and plan unlocks are tied to
            the account/email used at purchase and are not transferable except at our
            discretion. Flow Tweaks reserves the right to refuse service to anyone at any time.
          </p>
        </Section>

        <Section title="9. Privacy">
          <p>
            We collect the minimum personal information needed to run your account and deliver
            support: your email address (to log you in, confirm your purchase, and contact you
            about your order), your name and Discord username if you provide them, and the
            content of messages and files you send us through support chat. Order submissions
            also log the IP address and browser user-agent behind them, for fraud prevention and
            troubleshooting.
          </p>
          <p>
            We never see or store your card details. Payment is entered directly into the hosted
            checkout page run by Stripe; we only receive the plan you bought, the amount charged,
            and a Stripe transaction reference. We do not sell your personal information to third
            parties.
          </p>
          <p>
            The full detail — every category we collect, the analytics and advertising tools we
            use, who we share data with, how long we keep it, and how to get a copy or have it
            deleted — lives in our{" "}
            <Link
              to="/privacy"
              className="text-orange-500 hover:text-orange-400 underline underline-offset-4 decoration-orange-500/40"
              data-testid="terms-privacy-link"
            >
              Privacy Policy
            </Link>
            . Where this section and that policy differ, the Privacy Policy governs.
          </p>
        </Section>

        <Section title="10. Governing Law">
          <p>
            This Agreement is governed by the laws of the State of Missouri, United States. Any
            dispute is subject to the exclusive jurisdiction of the state and federal courts
            located in Missouri.
          </p>
        </Section>

        <Section title="Support Policy">
          <p>
            If you experience any issues related to your optimization, reach out to us
            on Discord and we will resolve it at no additional charge. Support is
            provided via Discord only.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            For all inquiries, support requests, or questions please contact us
            through our{" "}
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-500 hover:text-orange-400 underline underline-offset-4 decoration-orange-500/40"
              data-testid="terms-discord-link"
            >
              Discord server
            </a>
            .
          </p>
        </Section>

        <div className="mt-20 pt-8 border-t border-white/5 text-xs tracking-[0.2em] uppercase text-zinc-600">
          © {new Date().getFullYear()} Flow Tweaks. All rights reserved.
        </div>
      </main>
    </div>
  );
}

